export interface SourceCsvRow {
  name?: string | null;
  type?: string | null;
  url?: string | null;
  status?: string | null;
}

type CsvWritableFile = {
  write: (data: Blob) => Promise<void>;
  close: () => Promise<void>;
};

type CsvFileHandle = {
  createWritable: () => Promise<CsvWritableFile>;
};

type WindowWithSaveFilePicker = Window &
  typeof globalThis & {
    showSaveFilePicker?: (options: {
      suggestedName: string;
      types: Array<{
        description: string;
        accept: Record<string, string[]>;
      }>;
    }) => Promise<CsvFileHandle>;
  };

function escapeCsvField(value?: string | number | null): string {
  const text = value === undefined || value === null ? '' : String(value);
  if (!/[",\r\n]/.test(text)) return text;

  return `"${text.replace(/"/g, '""')}"`;
}

function serializeCsvRow(
  values: Array<string | number | null | undefined>,
): string {
  return values.map(escapeCsvField).join(',');
}

export function buildSourcesCsv(rows: SourceCsvRow[]): string {
  const header = serializeCsvRow(['Name', 'Type', 'URL', 'Connection Status']);
  const csvRows = rows.map((row) =>
    serializeCsvRow([row.name, row.type, row.url, row.status]),
  );

  return [header, ...csvRows, ''].join('\r\n');
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');

  anchor.href = url;
  anchor.download = filename;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export async function saveCsvBlob(blob: Blob, filename: string) {
  const showSaveFilePicker = (window as WindowWithSaveFilePicker)
    .showSaveFilePicker;

  if (showSaveFilePicker) {
    try {
      const fileHandle = await showSaveFilePicker({
        suggestedName: filename,
        types: [
          {
            description: 'CSV file',
            accept: {
              'text/csv': ['.csv'],
            },
          },
        ],
      });
      const writable = await fileHandle.createWritable();
      await writable.write(blob);
      await writable.close();
      return;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return;
      }

      throw error;
    }
  }

  downloadBlob(blob, filename);
}
